/**
 * 固定タイムステップ（CLAUDE.md §4.5）。
 * DT は絶対に可変にしない。速度変更は「1フレームで何回 tick するか」で行う。
 */
import { PLAY_WEAR, SWAT_WEAR, maybeGathering, maybeTussle, updateCat, wearRate } from './cat';
import { COMFORT_INTERVAL, SETTLE_SEC, checkComfort, pickArrivalSpecies } from './comfort';
import { checkFloorOpening } from './floors';
import {
  World,
  arriveNewCat,
  bumpTraffic,
  graphFor,
  speciesOf,
  wearFixture,
  wearItem,
  wearSalvage,
} from './world';

export const FIXED_DT = 1 / 60;

/** 猫がその場所で過ごすと、これだけで思い出が見つかる（シム内秒） */
export const MEMORY_DWELL = 40;

export function tick(world: World): void {
  const state = world.state;
  state.time += FIXED_DT;
  for (const cat of state.cats) {
    const graph = graphFor(world, cat.species);
    const before = cat.node;
    updateCat(state, graph, cat, speciesOf(world, cat.species), FIXED_DT, state.rng);
    // どこが動線になっているかの記録（デバッグ表示用。状態ではない）
    if (cat.node !== before) bumpTraffic(world, cat.node);

    // じゃれている物を壊していく。これが資材の主な出所（§3.4）
    if (cat.busyKind === 'play' && cat.toy !== null) {
      const broke = wearSalvage(world, cat.toy, PLAY_WEAR * FIXED_DT);
      if (broke) {
        cat.toy = null;
        cat.busy = 0;
        cat.busyKind = null;
      }
    }

    // 壁の飾りを叩き落とす。届く高さは猫の種類で違う（§3.3 / §3.4）
    if (cat.busyKind === 'swat' && cat.swat !== null) {
      const fell = wearFixture(world, cat.swat, SWAT_WEAR * FIXED_DT);
      if (fell) {
        cat.swat = null;
        cat.busy = 0;
        cat.busyKind = null;
      }
    }

    // 足元の設置物を傷ませる（§3.5）。壊れたら資材に戻り、経路は捨てられる
    const rate = wearRate(cat);
    if (rate > 0) {
      const grid = state.grid;
      const c = cat.node % grid.cols;
      const r = (cat.node / grid.cols) | 0;
      // 乗っている物（寝床）と、支えになっている物（箱）の両方が傷む
      wearItem(world, c, r, rate * FIXED_DT);
      wearItem(world, c, r + 1, rate * FIXED_DT);
    }
  }
  maybeTussle(state, FIXED_DT, state.rng);
  maybeGathering(state, FIXED_DT, state.rng);
  updateMemories(world);
  updateArrivals(world);
}

/**
 * 思い出（§1 裏テーマ）。
 *
 * 猫たちは、この建物が廃墟になる前からここに住んでいた。
 * その頃の物は、**猫が長く過ごした場所**に見つかる。
 * 探索させない。プレイヤーは猫が居着くのを眺めているだけでよい（§2.5）。
 *
 * 見つかった物はその場に残る。ビルそのものが記録になるので、
 * 一覧画面は作らない（作った瞬間「集める作業」になる。§2.5）。
 */
function updateMemories(world: World): void {
  const state = world.state;
  const cols = state.grid.cols;
  for (const memory of state.memories) {
    if (memory.found || memory.trigger.on !== 'dwell') continue;
    let resting = 0;
    for (const cat of state.cats) {
      if (cat.mode !== 'idle' || cat.busyKind === null) continue;
      const c = cat.node % cols;
      const r = (cat.node / cols) | 0;
      if (Math.abs(c - memory.c) <= 1 && r === memory.r) resting++;
    }
    if (resting === 0) continue;
    memory.dwell += FIXED_DT * resting;
    if (memory.dwell >= MEMORY_DWELL) {
      memory.found = true;
      world.justFound.push(memory);
      world.dirty = true;
    }
  }
}

/**
 * 快適な状態が続いている間だけ、外から猫が来る（§3.6）。
 * 猫は去らないし上限も設けない。条件が崩れると来なくなるだけで頭打ちになる（§10.3）。
 */
function updateArrivals(world: World): void {
  const state = world.state;
  if (state.time >= state.nextComfortCheck) {
    state.nextComfortCheck = state.time + COMFORT_INTERVAL;
    // 判定は「次に来たがっている猫」の目で行う（§3.3）
    world.lastComfort = checkComfort(world, state.nextArrival);
    // 上の階に手が届いたら、その階の元用途に応じた拾い物が出る（§3.7）
    const opened = checkFloorOpening(world);
    if (opened.length) world.justOpened.push(...opened);
  }
  const comfort = world.lastComfort;
  if (!comfort) return;

  if (comfort.ok) {
    state.settle += FIXED_DT;
    if (state.settle >= SETTLE_SEC) {
      state.settle = 0;
      arriveNewCat(world, state.nextArrival);
      state.nextArrival = pickArrivalSpecies(world, state.rng);
      world.lastComfort = checkComfort(world, state.nextArrival);
    }
  } else {
    // 崩れたら、溜まっていた分は少しずつ失われる
    state.settle = Math.max(0, state.settle - FIXED_DT * 2);
  }
}

export function tickTimes(world: World, n: number): void {
  for (let i = 0; i < n; i++) tick(world);
}
